import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { PageHeader, SectionCard, Pill, KpiTile } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth, hasPermission, hasRole } from "../context/AuthContext";
import { formatInr } from "../lib/accountsSettlement";
import { formatIndiaDate } from "../lib/indiaTime";
import { canTallySyncApprove } from "../lib/tallyApproval";
import { useTallyApproval } from "../context/TallyApprovalContext";
import { ArrowLeft, Check, Loader2, RefreshCw, X } from "lucide-react";

const TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "MATERIAL", label: "Purchase / material" },
  { value: "FREIGHT", label: "Freight" },
  { value: "SALES", label: "Sales" },
  { value: "RECEIPT", label: "Receipt" },
  { value: "CASH", label: "Payment" },
  { value: "JOURNAL", label: "Journal" },
];

function formatDate(value) {
  if (!value) return "—";
  try {
    return formatIndiaDate(value, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return String(value).slice(0, 10);
  }
}

function approvalTone(status) {
  const s = String(status || "").toUpperCase();
  if (s === "PENDING_APPROVAL") return "warning";
  if (s === "APPROVED") return "success";
  if (s === "REJECTED") return "danger";
  return "muted";
}

function legacyTone(badge) {
  if (badge === "SYNCED") return "success";
  if (badge === "SKIPPED") return "muted";
  return "warning";
}

export default function TallyApprovalQueuePage() {
  const { user } = useAuth();
  const canApprove = canTallySyncApprove(user, { hasPermission, hasRole });
  const { setPendingCount } = useTallyApproval();
  const [tab, setTab] = useState("POST_SEPT");
  const [status, setStatus] = useState("PENDING_APPROVAL");
  const [syncStatus, setSyncStatus] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [query, setQuery] = useState("");
  const queryRef = useRef("");
  queryRef.current = query;
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showXml, setShowXml] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectIds, setRejectIds] = useState([]);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const period = tab;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        period,
        voucher_type: typeFilter || undefined,
        q: queryRef.current || undefined,
        limit: 500,
      };
      if (period === "POST_SEPT") {
        params.status = status;
      } else {
        params.status = "ALL";
        if (syncStatus) params.sync_status = syncStatus;
      }
      const { data } = await api.get("/tally/approval-queue", { params });
      setItems(data?.items || []);
      setTotal(data?.count ?? 0);
      setKpis(data?.kpis || {});
      if (period === "POST_SEPT" && data?.kpis?.pending != null) {
        setPendingCount(Number(data.kpis.pending) || 0);
      }
      setSelected(new Set());
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [period, status, syncStatus, typeFilter, setPendingCount]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openPreview = async (id) => {
    setPreviewLoading(true);
    setShowXml(false);
    try {
      const { data } = await api.get(`/tally/vouchers/${id}/preview`);
      setPreview(data);
    } catch (err) {
      toast.error(extractError(err));
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allIds = useMemo(() => items.map((r) => r.id), [items]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(allIds));
  };

  const runBulk = async (action, ids, extra = {}) => {
    if (!canApprove) return;
    const voucherIds = ids?.length ? ids : [...selected];
    if (!voucherIds.length) {
      toast.error("Select at least one voucher");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/tally/approval-queue/bulk-action", {
        voucher_ids: voucherIds,
        action,
        queue_sync: action === "APPROVE" || action === "SYNC",
        ...extra,
      });
      const failed = data?.failed?.length || 0;
      const done = data?.processed?.length || 0;
      const ts = data?.tally_sync;
      if (failed && !done) {
        toast.error(data.failed[0]?.detail || "Bulk action failed");
      } else {
        toast.success(
          `${done} voucher${done === 1 ? "" : "s"} updated` +
            (ts?.queued ? ` · queued ${ts.queued} to Tally` : "") +
            (failed ? ` · ${failed} skipped` : ""),
        );
      }
      setRejectOpen(false);
      setRejectReason("");
      setPreview(null);
      await reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const approveOne = (row) => runBulk("APPROVE", [row.id]);
  const syncOne = (row) => runBulk("SYNC", [row.id]);
  const skipOne = (row) => runBulk("MARK_MANUALLY_SYNCED", [row.id], { remark: "Already posted in Tally (manual)" });

  return (
    <div className="space-y-5" data-testid="tally-approvals-page">
      <PageHeader
        eyebrow="Finance"
        title="Tally approvals"
        description="September 2026 onward entries must be approved here before Tally sync. Pre-September vouchers can be synced or marked as already posted without filling this queue."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/accounts/tally-mapping">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                Tally mapping
              </Link>
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={reload} disabled={loading}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Reload
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-1 border-b border-border pb-px">
        {[
          { id: "POST_SEPT", label: "September 2026 onward (Active queue)" },
          { id: "PRE_SEPT", label: "Pre-September legacy entries" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              setTab(t.id);
              setSelected(new Set());
            }}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-bansal-ink text-foreground font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tally-approvals-tab-${t.id.toLowerCase()}`}
          >
            {t.label}
            {t.id === "POST_SEPT" && kpis.pending ? ` (${kpis.pending})` : ""}
          </button>
        ))}
      </div>

      {tab === "POST_SEPT" ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiTile label="Pending approvals (Sept+)" value={kpis.pending ?? "—"} accent="amber" />
          <KpiTile label="Approved & synced" value={kpis.approved_synced ?? "—"} accent="emerald" />
          <KpiTile label="Sync failed / resync required" value={kpis.sync_failed ?? "—"} accent="rose" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiTile label="Not synced" value={kpis.not_synced ?? "—"} />
          <KpiTile label="Synced" value={kpis.synced ?? "—"} accent="emerald" />
          <KpiTile label="Skipped / already in Tally" value={kpis.skipped ?? "—"} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {tab === "POST_SEPT" ? (
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="PENDING_APPROVAL">Pending approval</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="ALL">All Sept+ statuses</option>
          </select>
        ) : (
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={syncStatus}
            onChange={(e) => setSyncStatus(e.target.value)}
          >
            <option value="">All sync states</option>
            <option value="NOT_SYNCED">Not synced</option>
            <option value="SYNCED">Synced</option>
            <option value="SKIPPED">Skipped / manual</option>
            <option value="FAILED">Failed</option>
          </select>
        )}
        <select
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value || "all"} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <Input
          className="max-w-[16rem]"
          placeholder="Code, party, gate pass, or id"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") reload();
          }}
        />
        <span className="text-xs text-muted-foreground">{total} voucher{total === 1 ? "" : "s"}</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-12 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading queue…
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-10 text-center">No vouchers in this filter.</p>
      ) : (
        <SectionCard title={tab === "POST_SEPT" ? "Active queue" : "Legacy vouchers"} className="overflow-hidden">
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 w-10">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all" />
                  </th>
                  <th className="px-3 py-2">Voucher</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Party / ledger</th>
                  <th className="px-3 py-2 text-right">Net ₹</th>
                  <th className="px-3 py-2">Gate pass / truck</th>
                  <th className="px-3 py-2">Created by</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(row.id)}
                        onChange={() => toggleRow(row.id)}
                        aria-label={`Select ${row.code}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{row.code}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{formatDate(row.txn_date || row.created_at)}</td>
                    <td className="px-3 py-2 text-xs">{row.voucher_type}</td>
                    <td className="px-3 py-2 text-xs max-w-[12rem] truncate" title={row.ledger_summary || row.party_name}>
                      {row.ledger_summary || row.party_name || "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{formatInr(row.net_payable)}</td>
                    <td className="px-3 py-2 text-xs">
                      <div>{row.gate_pass_code || "—"}</div>
                      {row.vehicle_number ? (
                        <div className="text-muted-foreground">{row.vehicle_number}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-xs">{row.created_by || "—"}</td>
                    <td className="px-3 py-2">
                      {tab === "PRE_SEPT" ? (
                        <Pill tone={legacyTone(row.legacy_badge)}>{row.legacy_badge || row.tally_sync_status}</Pill>
                      ) : (
                        <Pill tone={approvalTone(row.sync_approval_status)}>{row.sync_approval_status}</Pill>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => openPreview(row.id)}>
                          Preview
                        </Button>
                        {tab === "POST_SEPT" && canApprove && row.sync_approval_status === "PENDING_APPROVAL" ? (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              className="h-7 text-xs bg-emerald-700 hover:bg-emerald-800 text-white"
                              disabled={busy}
                              onClick={() => approveOne(row)}
                            >
                              Approve &amp; Sync
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              className="h-7 text-xs"
                              disabled={busy}
                              onClick={() => {
                                setRejectIds([row.id]);
                                setRejectOpen(true);
                              }}
                            >
                              Reject
                            </Button>
                          </>
                        ) : null}
                        {tab === "PRE_SEPT" && canApprove ? (
                          <>
                            {row.legacy_badge !== "SYNCED" && row.legacy_badge !== "SKIPPED" ? (
                              <Button type="button" size="sm" className="h-7 text-xs" disabled={busy} onClick={() => syncOne(row)}>
                                Sync
                              </Button>
                            ) : null}
                            {row.legacy_badge !== "SKIPPED" ? (
                              <Button type="button" size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => skipOne(row)}>
                                Mark manual
                              </Button>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {selected.size > 0 && canApprove ? (
        <div className="sticky bottom-16 lg:bottom-4 z-20 rounded-lg border border-border bg-card/95 backdrop-blur shadow-lg px-4 py-3 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{selected.size} selected</span>
          {tab === "POST_SEPT" ? (
            <>
              <Button type="button" size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={busy} onClick={() => runBulk("APPROVE")}>
                <Check className="h-3.5 w-3.5 mr-1" />
                Approve &amp; Sync selected
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={() => {
                  setRejectIds([...selected]);
                  setRejectOpen(true);
                }}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Reject selected
              </Button>
            </>
          ) : (
            <>
              <Button type="button" size="sm" disabled={busy} onClick={() => runBulk("SYNC")}>
                Sync selected
              </Button>
              <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => runBulk("MARK_MANUALLY_SYNCED", null, { remark: "Already posted in Tally (manual)" })}>
                Mark selected as manually synced in Tally
              </Button>
            </>
          )}
        </div>
      ) : null}

      {!canApprove ? (
        <p className="text-sm text-muted-foreground">
          Approve / reject is limited to Administrator, Mill Manager, and Accounts Manager.
        </p>
      ) : null}

      <Dialog open={Boolean(preview) || previewLoading} onOpenChange={(open) => { if (!open) setPreview(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tally preview{preview?.voucher?.code ? ` · ${preview.voucher.code}` : ""}</DialogTitle>
            <DialogDescription>
              Debit / credit ledgers and bill-wise refs that will be sent to Tally.
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Building preview…
            </div>
          ) : preview ? (
            <div className="space-y-3 text-sm">
              <p className="rounded-md border border-border bg-muted/40 px-3 py-2">{preview.headline}</p>
              {preview.effects?.length ? (
                <ul className="space-y-1">
                  {preview.effects.map((fx, i) => (
                    <li key={i} className="text-xs text-foreground">• {fx.text}</li>
                  ))}
                </ul>
              ) : null}
              {preview.ledgers?.length ? (
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground text-left">
                    <tr>
                      <th className="py-1">Ledger</th>
                      <th className="py-1 text-right">Debit</th>
                      <th className="py-1 text-right">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.ledgers.map((ln, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="py-1">{ln.ledger}{ln.unmapped ? " (map this)" : ""}</td>
                        <td className="py-1 text-right">{ln.debit ? formatInr(ln.debit) : ""}</td>
                        <td className="py-1 text-right">{ln.credit ? formatInr(ln.credit) : ""}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-border font-medium">
                      <td className="py-1">Totals</td>
                      <td className="py-1 text-right">{formatInr(preview.debit_total)}</td>
                      <td className="py-1 text-right">{formatInr(preview.credit_total)}</td>
                    </tr>
                  </tbody>
                </table>
              ) : null}
              <button type="button" className="text-xs underline text-muted-foreground" onClick={() => setShowXml((v) => !v)}>
                {showXml ? "Hide raw Tally XML" : "Show raw Tally XML"}
              </button>
              {showXml ? (
                preview.xml ? (
                  <textarea readOnly className="w-full h-40 font-mono text-[11px] rounded-md border border-input bg-background p-2" value={preview.xml} />
                ) : (
                  <p className="text-xs text-muted-foreground">{preview.xml_error || "XML not available."}</p>
                )
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject voucher{rejectIds.length > 1 ? "s" : ""}</DialogTitle>
            <DialogDescription>
              The creator is notified with this reason. The voucher stays in accounts as a draft until corrected.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="tally-reject-reason">Reason</Label>
            <Textarea
              id="tally-reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Incorrect freight rate, missing party GST, …"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button
              type="button"
              variant="destructive"
              disabled={busy || rejectReason.trim().length < 3}
              onClick={() => runBulk("REJECT", rejectIds, { rejection_reason: rejectReason.trim() })}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
